import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { articles, getLatestArticles } from '@/mocks/articles';
import { faqs } from '@/mocks/faq';
import { useState } from 'react';

export default function Home() {
  const latestArticles = getLatestArticles(6);
  const featuredArticles = articles.slice(0, 3);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const jsonLdWebSite = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '秘密教学漫画',
    url: 'https://www.mimijiaoxue-manhua.com',
    description: '探索「秘密教学漫画」的精彩世界，汇聚最新章节更新、剧情解析、角色介绍与粉丝热议。',
    inLanguage: 'zh-CN',
  };

  const jsonLdOrganization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '秘密教学漫画内容平台',
    url: 'https://www.mimijiaoxue-manhua.com',
    description: '专注于秘密教学漫画的内容导览、深度解析与粉丝社区平台',
  };

  const jsonLdFAQ = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="秘密教学漫画 - 最全合集与深度解析 | 最新章节、剧情解析、角色介绍"
        description="探索「秘密教学漫画」的精彩世界，汇聚最新章节更新、剧情解析、角色介绍与粉丝热议。无论你是初次接触还是资深读者，这里提供秘密教学漫画最完整的内容导览与深度评测。"
        keywords="秘密教学漫画,秘密教学漫画最新章节,秘密教学漫画剧情解析,秘密教学漫画角色介绍,秘密教学漫画在线阅读,秘密教学漫画合集,秘密教学漫画评测"
        canonicalUrl="https://www.mimijiaoxue-manhua.com/"
        ogTitle="秘密教学漫画 - 最全合集与深度解析"
        ogDescription="探索「秘密教学漫画」的精彩世界，最完整的内容导览与深度评测。"
        ogType="website"
        ogUrl="https://www.mimijiaoxue-manhua.com/"
      />

      <script type="application/ld+json">{JSON.stringify(jsonLdWebSite)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdOrganization)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdFAQ)}</script>

      <Navbar />
      <ScrollToTop />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20beautiful%20artistic%20illustration%20of%20an%20elegant%20reading%20space%20with%20Japanese%20manga%20books%20artistically%20arranged%20on%20a%20wooden%20shelf%2C%20warm%20sunlight%20streaming%20through%20a%20large%20window%20creating%20soft%20shadows%2C%20a%20cozy%20armchair%20with%20a%20throw%20blanket%2C%20potted%20plants%20adding%20life%2C%20cream%20and%20warm%20amber%20color%20palette%2C%20serene%20and%20inviting%20atmosphere%2C%20editorial%20photography%20quality%2C%20clean%20composition%2C%20soft%20natural%20lighting&width=1600&height=900&seq=hero-manga-main&orientation=landscape"
            alt="秘密教学漫画 - 精彩内容导览平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
            title="秘密教学漫画内容平台"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
            <span className="inline-block px-4 py-1.5 text-xs md:text-sm rounded-full bg-accent-500/20 backdrop-blur-sm text-background-50 border border-accent-400/30 mb-4 md:mb-6 whitespace-nowrap">
              <i className="ri-sparkling-line mr-1.5"></i>
              秘密教学漫画 · 最全内容导览平台
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight max-w-3xl">
              探索<strong className="text-accent-300">秘密教学漫画</strong>的精彩世界
            </h1>
            <p className="mt-4 md:mt-6 text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
              汇聚最新章节更新、深度剧情解析、角色全面介绍与粉丝精彩热议
            </p>
            <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/category/latest-chapters"
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-flashlight-line mr-1.5"></i>
                查看最新章节
              </Link>
              <Link
                to="/about"
                className="px-6 py-2.5 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-full font-medium text-sm border border-background-50/25 hover:bg-background-50/25 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-information-line mr-1.5"></i>
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                内容分类导航
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                按分类浏览秘密教学漫画的精彩内容
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <span className="mt-3 font-heading text-sm md:text-base font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="mt-1 text-xs text-foreground-400 text-center leading-relaxed hidden sm:block">
                    {cat.description}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  精选推荐
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  编辑精选的秘密教学漫画深度内容
                </p>
              </div>
              <Link
                to="/category/collections"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  最新更新
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  秘密教学漫画最新内容一次掌握
                </p>
              </div>
              <Link
                to="/category/latest-chapters"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                常见问题
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                关于秘密教学漫画的常见疑问解答
              </p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={faq.id}
                  className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <h4 className="font-heading text-sm md:text-base font-semibold text-foreground-800 pr-4">
                      {faq.question}
                    </h4>
                    <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center">
                      <i
                        className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-300 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16 bg-accent-500/10">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              加入秘密教学漫画的精彩世界
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              无论你是初次接触还是资深读者，这里为你提供最完整的内容导览与深度评测
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/latest-chapters"
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                立即探索最新章节
              </Link>
              <Link
                to="/about"
                className="px-6 py-2.5 bg-background-50 text-foreground-800 rounded-full font-medium text-sm border border-background-300 hover:border-primary-300 hover:text-primary-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                了解关于我们
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}